// ----------------------
// Email Validation
// ----------------------
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MAX_EMAIL_LENGTH = 256;

export const validateEmail = (email: string): boolean => EMAIL_REGEX.test(email);

export const getEmailError = (email: string): string => {
  if (email.length > MAX_EMAIL_LENGTH) {
    return `Email must be under ${MAX_EMAIL_LENGTH} characters.`;
  }

  if (email.length > 0 && !validateEmail(email)) {
    return "Please enter a valid email address.";
  }
  return "";
};

// ----------------------
// Name Validation
// ----------------------
export const MAX_NAME_LENGTH = 50;

export const NAME_REGEX = /^[^\s].*$/;


export const getNameError = (
  name: string,
  fieldLabel: string,
  touched = false
): string => {
  if (!touched) return "";

  if (name.length > MAX_NAME_LENGTH) {
    return `${fieldLabel} must be under ${MAX_NAME_LENGTH} characters.`;
  }

  return "";
};

// ----------------------
// Advanced Name Helper
// ----------------------
export const MAX_ADV_NAME = 50;
export const sanitizeAdvName = (value: string) => value.slice(0, MAX_ADV_NAME);
export const getAdvNameHelper = (value: string) => `${value.length}/${MAX_ADV_NAME} characters`;

// ----------------------
// Address Validation
// ----------------------
export const MAX_ADDRESS_LENGTH = 50;

export const getAddressError = (value: string, touched: boolean = false) => {
  if (!touched) return { error: false, helperText: "" };
  if (!value) return { error: true, helperText: "Address is required." };
  if (value.length > MAX_ADDRESS_LENGTH) {
    return { error: true, helperText: `Maximum ${MAX_ADDRESS_LENGTH} characters allowed.` };
  }
  return { error: false, helperText: "" };
};

export const getAddressLine2Error = (value: string, touched: boolean = false) => {
  if (!touched) return { error: false, helperText: "" };
  if (value.length > MAX_ADDRESS_LENGTH) {
    return { error: true, helperText: `Maximum ${MAX_ADDRESS_LENGTH} characters allowed.` };
  }
  return { error: false, helperText: "" };
};

// ----------------------
// Phone Number Validation
// ----------------------
export const MAX_PHONE_LENGTH = 14;

// export const sanitizePhoneNumber = (value: string) => value.replace(/\D/g, "").slice(0, MAX_PHONE_LENGTH);

export const getPhoneNumberError = (value: string, touched: boolean = false) => {
  const digitsOnly = value.replace(/\D/g, "");
  if (!digitsOnly) return { error: false, helperText: "" };
  if (touched && digitsOnly.length !== 10)
    return { error: true, helperText: "Phone number must be 10 digits." };
  return { error: false, helperText: "" };
};

export function sanitizePhoneNumber(value: string): string {
  if (!value) return "";
  return value.replace(/\D/g, "").slice(0, MAX_PHONE_LENGTH);
}



// ----------------------
// City Validation
// ----------------------
export const MAX_CITY_LENGTH = 40;

export const getCityError = (value: string, touched: boolean = false) => {
  if (!touched) return { error: false, helperText: "" };
  if (!value) return { error: true, helperText: "City is required." };
  if (value.length > MAX_CITY_LENGTH) return { error: true, helperText: `Maximum ${MAX_CITY_LENGTH} characters allowed.` };
  return { error: false, helperText: "" };
};

// ----------------------
// ZIP Code Validation
// ----------------------
export const ZIP_LENGTH = 5;

export const sanitizeZip = (value: string) => value.replace(/\D/g, "").slice(0, ZIP_LENGTH);

export const getZipError = (value: string, touched: boolean = false) => {
  if (!touched) return { error: false, helperText: "" };
  if (!value) return { error: true, helperText: "ZIP is required." };
  if (value.length < ZIP_LENGTH) return { error: true, helperText: `Enter ${ZIP_LENGTH} digits` };
  return { error: false, helperText: "" };
};

// ----------------------
// ZIP Extension Validation
// ----------------------
export const ZIP_EXT_LENGTH = 4;

export const sanitizeZipExtension = (value: string) => value.replace(/\D/g, "").slice(0, ZIP_EXT_LENGTH);

export const getZipExtensionError = (value: string, touched: boolean = false) => {
  if (!touched) return { error: false, helperText: "" };
  if (value.length < ZIP_EXT_LENGTH)
    return { error: true, helperText: `Enter ${ZIP_EXT_LENGTH} digits` };
  return { error: false, helperText: "" };       
};

export const MAX_SUBJECT_LENGTH = 100;

export const getSubjectError = (subject: string, touched: boolean = false): string => {
  if (!touched) return "";
  
  if (subject.length > MAX_SUBJECT_LENGTH) {
    return `Subject must be under ${MAX_SUBJECT_LENGTH} characters.`;
  }
  
  if (subject && /^\s/.test(subject)) {
    return "Subject cannot start with a space.";
  }
  
  return "";
};

export const MAX_DESCRIPTION_LENGTH = 500;

export const getDescriptionError = (description: string, touched: boolean = false): string => {
  if (!touched) return "";
  
  if (description.length > MAX_DESCRIPTION_LENGTH) {
    return `Description must be under ${MAX_DESCRIPTION_LENGTH} characters.`;
  }
  
  if (description && /^\s/.test(description)) {
    return "Description cannot start with a space.";
  }
  
  return "";
};

export const MAX_PASSWORD_LENGTH = 128;

export const getPasswordError = (password: string, touched: boolean = false): string => {
  if (!touched) return "";
  
  if (password.length > MAX_PASSWORD_LENGTH) {
    return `Password must be under ${MAX_PASSWORD_LENGTH} characters.`;
  }
  
  if (password && /^\s/.test(password)) {
    return "Password cannot start with a space.";
  }
  
  return "";
};
